import React, { useState, useEffect, Fragment } from 'react'
import {
  DialogActions,
  Button,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography
} from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { ProductOriginsDDL } from '../../../components/Producto/ProductOrigins/ProductOriginsDDL'
import { useApi } from '../../../hooks/useApi'
import {
  getExternalStorageSkuMasiveTemplate,
  postExternalStorageMassive
} from './../../ProductAdmin/config/api'
// import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { useProductOrigins } from '../../../hooks'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import {
  sectionBox,
  uploadZone,
  downloadLink
} from './StylesModal/ExternalStorageMasiveModal.styles'
import { GenericDLL } from '../../../components/Almacen/UI/GenericDLL'

interface ExternalStorageMasiveModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
}

export const ExternalStorageMasiveModal: React.FC<
  ExternalStorageMasiveModalProps
> = ({ handleSnackbar, handleCloseModal }) => {
  const [productOriginId, setProductOriginId] = useState<number>(0)
  const [operationType, setOperationType] = useState<boolean | undefined>()
  const [selectedOriginName, seSelectedOriginName] = useState<string>('')
  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [fileName, setFileName] = useState<string>(
    'Ningún archivo seleccionado'
  )

  const isDisabled =
    productOriginId === 0 || !selectedFile || operationType === undefined
  const confirmModal = useModal('')

  // Estado para descargar template
  const { state: templateState, callApi: callDownloadTemplate } = useApi()
  const { state: createMassiveState, callApi: callCreateMassive } = useApi()

  const {
    getProductOrigins,
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const handleSelectFile = (event: any) => {
    const files = event.target.files
    if (files && files.length > 0) {
      setSelectedFile(files[0])
      setFileName(files[0].name)
    }
  }

  const handleDownloadTemplate = () => {
    callDownloadTemplate(getExternalStorageSkuMasiveTemplate())
      .then((response: any) => {
        const raw = window.atob(response.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], { type: response.mimeType })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch((error) => {
        handleSnackbar('Error al descargar el template: ' + error, false)
      })
  }

  const handleSave = () => {
    // Validaciones
    if (productOriginId === 0) {
      handleSnackbar('Debe seleccionar una marca', false)
      return
    }

    if (!selectedFile) {
      handleSnackbar('Debe seleccionar un archivo', false)
      return
    }

    const formData = new FormData()

    // Agregar los valores del formulario
    formData.append('productOriginId', productOriginId.toString())
    formData.append('externalStorage', String(operationType ?? false))
    // Agregar el archivo seleccionado
    formData.append('file', selectedFile)

    callCreateMassive(postExternalStorageMassive(formData))
      .then(() => {
        handleSnackbar('Carga realizada exitosamente', true)
        handleCloseModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  useEffect(() => {
    if (productOriginId && productOrigins) {
      const selected = productOrigins.find(
        (v) => v.productOriginId === productOriginId
      )
      seSelectedOriginName(selected?.name || '')
    } else {
      seSelectedOriginName('')
    }
  }, [productOriginId])

  useEffect(() => {
    // Cargar marcas al inicializar la página
    getProductOrigins()
  }, [])

  useEffect(() => {
    if (productOriginId === 0) {
      return // No cargar si no hay marca seleccionada
    }
  }, [operationType, productOriginId])

  const mixedOrigins = (productOrigins || []).filter(p => p.typeStorageId === 3)
  const noMixedOrigins = mixedOrigins.length === 0

  return (
    <Fragment>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Descripción */}
        <Box sx={sectionBox}>
          <Typography variant='body2' color='textSecondary'>
            Configura la asociación masiva para los productos seleccionados.
          </Typography>
        </Box>

        {/* Marca */}
        <Box sx={sectionBox}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Marca *
          </Typography>
          
          <GenericDLL
            fullWidth
            initialValues={mixedOrigins}
            onChange={(e: any) => setProductOriginId(e.target.value as number)}
            title='Seleccionar Marca'
            name='name'
            valueKey={'productOriginId'}
            value={productOriginId}
            variant='outlined'
            disabled={noMixedOrigins}
            error={productOriginId === 0}
            helperText={
              noMixedOrigins
                ? 'No existen marcas configuradas con almacenamiento mixto'
                : productOriginId === 0
                ? 'Seleccionar Marca'
                : ''
            }
          />
        </Box>

        {/* Tipo de operación */}
        <Box sx={sectionBox}>
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Almacenamiento externo
          </Typography>
          <RadioGroup
            row
            value={operationType}
            onChange={(e) => setOperationType(e.target.value === 'true')}
          >
            <FormControlLabel
              value='true'
              control={<Radio />}
              label='Activar'
            />
            <FormControlLabel
              value='false'
              control={<Radio />}
              label='Desactivar'
            />
          </RadioGroup>
        </Box>

        {/* Cargar archivo */}
        <Box sx={sectionBox}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
              SKUs específicos
            </Typography>
            <Box
              onClick={(e) => {
                e.preventDefault()
                if (templateState.status !== Status.Loading) {
                  handleDownloadTemplate()
                }
              }}
              sx={downloadLink}
            >
              <FileDownloadIcon fontSize='small' />
              <Typography variant='caption' sx={{ color: 'inherit' }}>
                {templateState.status === Status.Loading ? 'Descargando...' : 'Descargar Template'}
              </Typography>
            </Box>
          </Box>
          <Typography variant='body2' color='textSecondary'>
            Carga un archivo con los SKUs a los que desea aplicar la asociación.
          </Typography>
          <label htmlFor='file-upload' style={{ display: 'block' }}>
            <input
              id='file-upload'
              type='file'
              onChange={handleSelectFile}
              accept='.xls,.xlsx,.csv'
              style={{ display: 'none' }}
            />
            <Box component='span' sx={uploadZone(!!selectedFile)}>
              <UploadFileIcon
                sx={{
                  fontSize: 36,
                  color: selectedFile ? 'success.main' : 'grey.400'
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 500,
                  color: selectedFile ? 'success.main' : 'text.secondary'
                }}
              >
                {selectedFile
                  ? fileName
                  : 'Haz clic para seleccionar un archivo'}
              </Typography>
              <Typography variant='caption' color='text.disabled'>
                Formatos aceptados: .xls, .xlsx, .csv
              </Typography>
            </Box>
          </label>
        </Box>
      </Box>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} variant='outlined'>
          Cancelar
        </Button>
        <Button
          onClick={() =>
            confirmModal.openModal(
              'sm',
              undefined,
              `${operationType === false ? 'Eliminar' : 'Asociar'}  Almacenamiento externo`
            )
          }
          variant='contained'
          color='primary'
          disabled={isDisabled}
        >
          Guardar
        </Button>
      </DialogActions>
      <Modal
        open={confirmModal.isOpen}
        maxWidth={confirmModal.size}
        onClose={confirmModal.closeModal}
        title={confirmModal.title}
        loading={createMassiveState.status === Status.Loading}
        cancelButton={{
          action: () => confirmModal.closeModal(),
          title: 'Cancelar'
        }}
        successButton={{
          action: handleSave,
          title: 'Confirmar'
        }}
      >
        <Typography variant='body2' component={'h4'}>
          Se{' '}
          <Box component='span' sx={{ fontWeight: 'bold' }}>
            {operationType === false ? 'Eliminará' : 'Asociará'}
          </Box>{' '}
          Almacenamiento externo a los productos de la marca{' '}
          <Box component='span' sx={{ fontWeight: 'bold' }}>
            {selectedOriginName}
          </Box>
          . ¿Desea continuar?
        </Typography>
      </Modal>
    </Fragment>
  )
}
